import React from 'react';
import { Launch, Popup } from '@core/icons-react';
import { Link } from '../interactives/Link';
import type { LinkCellProps } from './types';

/**
 * LinkCell - Renders clickable links
 * 
 * Features:
 * - Fixed size (medium) for consistency
 * - Configurable color (strong, default, subtle)
 * - Configurable underline (dotted, solid, none)
 * - Configurable weight (default, bold)
 * - Icon visibility: 'hover', 'always', or 'none'
 * - Icon type: 'launch' (page nav) or 'popup' (modal)
 * - Click handler support
 */
export const LinkCell: React.FC<LinkCellProps> = ({ value, config, row }) => {
  if (value === null || value === undefined) {
    return null;
  }
  
  // Get the onClick handler
  const handleClick = config?.onClick 
    ? (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        config.onClick!(value, row);
      }
    : undefined;
  
  // Determine icon configuration
  const iconVisibility = config?.icon || 'none';
  const showIcon = iconVisibility !== 'none';
  const iconOnHover = iconVisibility === 'hover';
  
  // Determine which icon to show
  const iconType = config?.iconType || 'launch';
  const IconComponent = iconType === 'popup' ? Popup : Launch;
  
  const handleHover = config?.onHover
    ? () => config.onHover!(value, row)
    : undefined;

  const displayValue = String(value);
  
  return (
    <Link
      size="medium"
      href={config?.href || '#'}
      color={config?.color || 'default'}
      underline={config?.underline || 'dotted'}
      weight={config?.weight || 'bold'}
      response="link"
      icon={showIcon ? IconComponent : undefined}
      iconPosition="right"
      iconOnHover={iconOnHover}
      onClick={handleClick}
      onHover={handleHover}
      className={config?.className}
      title={displayValue}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block'
      }}
    >
      {displayValue}
    </Link>
  );
};

